/**
 * Buy 2 Get 2 — JavaScript Controller (v4)
 * Features: admin-managed category tabs, collection-based lazy loading,
 *           dynamic free price cap, sort, quick-look modal, cart drawer, confetti
 */
(function () {
  'use strict';

  const B2G2_MAX_PAID = 2;
  const B2G2_MAX_FREE = 2;

  class Buy2Get2 {
    constructor(config) {
      this.config = config || {};
      this.paidItems = [];
      this.freeItems = [];
      this.bundleId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      this.isAddingToCart = false;
      this.currentStep = 1;
      this.uidCounter = 0;

      this.cacheDOM();
      this.bindEvents();
      this.bindQuickLook();
      this.updateUI();
      this._initAjaxLoad();
    }

    /* ---- DOM Cache ---- */
    cacheDOM() {
      this.paidGrid = document.getElementById('b2g2-paid-grid');
      this.freeGrid = document.getElementById('b2g2-free-grid');
      this.bottomBar = document.getElementById('b2g2-bottom-bar');
      this.actionBtn = document.getElementById('b2g2-action-btn');
      this.barInfo = document.getElementById('b2g2-bar-info');
      this.totalEl = document.getElementById('b2g2-total');
      this.originalTotalEl = document.getElementById('b2g2-original-total');
      this.savingsBadge = document.getElementById('b2g2-savings-badge');
      this.paidCounter = document.getElementById('b2g2-paid-counter');
      this.freeCounter = document.getElementById('b2g2-free-counter');
      this.paidStepEl = document.getElementById('b2g2-paid-step');
      this.freeStepEl = document.getElementById('b2g2-free-step');
      this.progressStep1 = document.getElementById('b2g2-prog-step1');
      this.progressStep2 = document.getElementById('b2g2-prog-step2');
      this.progressLine = document.getElementById('b2g2-prog-line');
      this.modalOverlay = document.getElementById('b2g2-modal-overlay');

      this.slots = {
        paid: [
          document.querySelector('[data-slot="paid-0"]'),
          document.querySelector('[data-slot="paid-1"]'),
        ],
        free: [
          document.querySelector('[data-slot="free-0"]'),
          document.querySelector('[data-slot="free-1"]'),
        ],
      };
    }

    /* ---- Events ---- */
    bindEvents() {
      if (this.paidGrid) {
        this.paidGrid.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-b2g2-action]');
          if (!btn) return;
          e.stopPropagation();
          const card = btn.closest('.b2g2-card');
          if (!card) return;
          this.handleCardAction(card, 'paid', btn.dataset.b2g2Action);
        });
      }

      if (this.freeGrid) {
        this.freeGrid.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-b2g2-action]');
          if (!btn) return;
          e.stopPropagation();
          const card = btn.closest('.b2g2-card');
          if (!card) return;
          this.handleCardAction(card, 'free', btn.dataset.b2g2Action);
        });
      }

      if (this.bottomBar) {
        this.bottomBar.addEventListener('click', (e) => {
          const removeBtn = e.target.closest('.b2g2-slot-remove');
          if (!removeBtn) return;
          const slot = removeBtn.closest('.b2g2-slot');
          if (!slot) return;
          this.handleSlotRemove(slot);
        });
      }

      if (this.actionBtn) {
        this.actionBtn.addEventListener('click', () => this.handleActionClick());
      }
    }

    /* ---- Quick Look Modal ---- */
    bindQuickLook() {
      // Use event delegation so dynamically AJAX-loaded cards work
      [this.paidGrid, this.freeGrid].forEach((grid) => {
        if (!grid) return;
        grid.addEventListener('click', (e) => {
          const btn = e.target.closest('.b2g2-quick-look-btn');
          if (!btn) return;
          e.stopPropagation();
          const card = btn.closest('.b2g2-card');
          if (!card) return;
          this.openQuickLook(card);
        });
      });

      // Close modal
      if (this.modalOverlay) {
        this.modalOverlay.addEventListener('click', (e) => {
          if (e.target === this.modalOverlay || e.target.closest('.b2g2-modal-close')) {
            this.closeQuickLook();
          }
        });

        // Modal add to box button
        const modalAddBtn = this.modalOverlay.querySelector('.b2g2-modal-add-btn');
        if (modalAddBtn) {
          modalAddBtn.addEventListener('click', () => {
            const card = this._currentModalCard;
            if (!card) return;
            const type = card.closest('#b2g2-paid-grid') ? 'paid' : 'free';
            const modalVarSelectors = this.modalOverlay.querySelector('.b2g2-modal-variant-selectors');
            const highlightSelectors = () => {
              if (!modalVarSelectors) return;
              modalVarSelectors.classList.add('b2g2-variant-highlight');
              setTimeout(() => modalVarSelectors.classList.remove('b2g2-variant-highlight'), 1500);
            };
            const mv = this._modalSelectedVariant;

            // When the modal shows option chips, only the variant chosen there may be
            // added (the card can still hold an earlier variant, e.g. after a sold-out pick).
            if (modalVarSelectors && !mv) {
              highlightSelectors();
              return;
            }

            // Block if still no variant selected
            if (!mv && (!card.dataset.variantId || card.classList.contains('b2g2-needs-variant'))) {
              highlightSelectors();
              return;
            }

            const candidate = {
              variantId: mv ? String(mv.id) : card.dataset.variantId,
              productId: card.dataset.productId,
              price: mv ? parseInt(mv.price, 10) : parseInt(card.dataset.price, 10),
              comparePrice: mv ? (parseInt(mv.compare_at_price, 10) || 0) : (parseInt(card.dataset.comparePrice, 10) || 0),
              title: card.dataset.title,
              image: card.dataset.image,
              element: card,
            };

            // Validate before touching the card, so a rejected variant is not written to it.
            // The modal stays open so the customer can choose another option.
            const block = this._addBlockReason(type, candidate);
            if (block) {
              this._showMessage(block.full
                ? 'You have already picked ' + (type === 'paid' ? B2G2_MAX_PAID + ' products' : B2G2_MAX_FREE + ' free items') + '. Remove one to add this.'
                : block.message);
              this.shakeElement(modalAddBtn);
              return;
            }

            // Apply the modal's variant to the card, then add it
            if (mv) {
              card.dataset.variantId = candidate.variantId;
              card.dataset.price = candidate.price;
              card.dataset.comparePrice = candidate.comparePrice;
              card.classList.remove('b2g2-needs-variant');
            }

            if (this.addItem(type, candidate)) this.closeQuickLook();
          });
        }
      }
    }

    openQuickLook(card) {
      if (!this.modalOverlay) return;
      this._currentModalCard = card;

      const title = card.dataset.title;
      const price = parseInt(card.dataset.price, 10);
      const comparePrice = parseInt(card.dataset.comparePrice, 10) || 0;
      const imagesJson = card.dataset.images || '[]';
      const description = card.dataset.description || '';
      const isFree = card.classList.contains('b2g2-card--free');

      let images;
      try { images = JSON.parse(imagesJson); } catch (e) { images = []; }
      if (images.length === 0) images = [card.dataset.image];

      // Fill modal content
      const titleEl = this.modalOverlay.querySelector('.b2g2-modal-product-title');
      const priceEl = this.modalOverlay.querySelector('.b2g2-modal-price');
      const compPriceEl = this.modalOverlay.querySelector('.b2g2-modal-compare-price');
      const descEl = this.modalOverlay.querySelector('.b2g2-modal-description');
      const mainImg = this.modalOverlay.querySelector('.b2g2-modal-main-img');
      const thumbsContainer = this.modalOverlay.querySelector('.b2g2-modal-thumbs');

      if (titleEl) titleEl.textContent = title;
      if (priceEl) {
        priceEl.textContent = isFree ? 'FREE' : this.formatMoney(price);
        priceEl.style.color = isFree ? '#2e7d32' : '#1a1a1a';
      }
      if (compPriceEl) {
        if (comparePrice > price || isFree) {
          compPriceEl.textContent = this.formatMoney(isFree ? price : comparePrice);
          compPriceEl.style.display = 'inline';
        } else {
          compPriceEl.style.display = 'none';
        }
      }
      if (descEl) descEl.innerHTML = description;
      if (mainImg) mainImg.src = images[0];

      // Thumbnails
      if (thumbsContainer) {
        thumbsContainer.innerHTML = '';
        images.forEach((src, i) => {
          const thumb = document.createElement('img');
          thumb.className = 'b2g2-modal-thumb' + (i === 0 ? ' is-active' : '');
          thumb.src = src;
          thumb.alt = title + ' image ' + (i + 1);
          thumb.addEventListener('click', () => {
            if (mainImg) mainImg.src = src;
            thumbsContainer.querySelectorAll('.b2g2-modal-thumb').forEach((t) => t.classList.remove('is-active'));
            thumb.classList.add('is-active');
          });
          thumbsContainer.appendChild(thumb);
        });
      }

      // Build variant selectors in modal
      const modalDetailsEl = this.modalOverlay.querySelector('.b2g2-modal-details');
      const existingModalVariants = modalDetailsEl ? modalDetailsEl.querySelector('.b2g2-modal-variant-selectors') : null;
      if (existingModalVariants) existingModalVariants.remove();

      const modalAddBtn = modalDetailsEl ? modalDetailsEl.querySelector('.b2g2-modal-add-btn') : null;
      const variantsJsonStr = card.dataset.variantsJson || '';
      const optionsJsonStr = card.dataset.optionsJson || '';
      let variants = [];
      let options = [];
      try { variants = JSON.parse(variantsJsonStr); } catch (e) {}
      try { options = JSON.parse(optionsJsonStr); } catch (e) {}

      // Reset modal selected variant state
      this._modalSelectedVariant = null;

      if (variants.length > 1 && options.length > 0 && modalDetailsEl) {
        let modalVarHtml = '<div class="b2g2-modal-variant-selectors">';
        options.forEach((opt, idx) => {
          const optName = opt.name || ('Option ' + (idx + 1));
          const values = opt.values || [];
          if (values.length <= 1) return;
          modalVarHtml += '<div class="b2g2-modal-variant-group">';
          modalVarHtml += '<span class="b2g2-modal-variant-label">' + this._esc(optName) + '</span>';
          modalVarHtml += '<div class="b2g2-modal-variant-chips" data-option-index="' + (idx + 1) + '">';
          values.forEach((val) => {
            modalVarHtml += '<button type="button" class="b2g2-modal-chip" data-value="' + this._esc(val) + '">' + this._esc(val) + '</button>';
          });
          modalVarHtml += '</div></div>';
        });
        modalVarHtml += '</div>';

        if (modalAddBtn) {
          modalAddBtn.insertAdjacentHTML('beforebegin', modalVarHtml);
          modalAddBtn.textContent = 'SELECT OPTIONS';
          modalAddBtn.classList.add('b2g2-btn-disabled');
        }

        // Bind chip click events
        const chipGroups = modalDetailsEl.querySelectorAll('.b2g2-modal-variant-chips');
        chipGroups.forEach((group) => {
          group.addEventListener('click', (e) => {
            const chip = e.target.closest('.b2g2-modal-chip');
            if (!chip) return;
            // Toggle selection in this group
            group.querySelectorAll('.b2g2-modal-chip').forEach((c) => c.classList.remove('is-active'));
            chip.classList.add('is-active');
            // Check if all options selected, find matching variant
            this._resolveModalVariant(card, variants, modalDetailsEl, priceEl, compPriceEl);
          });
        });
      } else {
        // Single variant or no options — just show ADD TO BOX
        if (modalAddBtn) {
          modalAddBtn.textContent = 'ADD TO BOX';
          modalAddBtn.classList.remove('b2g2-btn-disabled');
        }
      }

      this.modalOverlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    _resolveModalVariant(card, variants, modalDetailsEl, priceEl, compPriceEl) {
      const chipGroups = modalDetailsEl.querySelectorAll('.b2g2-modal-variant-chips');
      const selectedOptions = {};
      let allSelected = true;

      chipGroups.forEach((group) => {
        const idx = group.dataset.optionIndex;
        const active = group.querySelector('.b2g2-modal-chip.is-active');
        if (active) {
          selectedOptions['option' + idx] = active.dataset.value;
        } else {
          allSelected = false;
        }
      });

      const modalAddBtn = modalDetailsEl.querySelector('.b2g2-modal-add-btn');
      const isFree = card.classList.contains('b2g2-card--free');

      if (!allSelected) {
        this._modalSelectedVariant = null;
        if (modalAddBtn) {
          modalAddBtn.textContent = 'SELECT OPTIONS';
          modalAddBtn.classList.add('b2g2-btn-disabled');
        }
        return;
      }

      // Find matching variant
      const matched = variants.find((v) => {
        for (const key in selectedOptions) {
          if (selectedOptions[key] && v[key] !== selectedOptions[key]) return false;
        }
        return true;
      });

      if (matched && matched.available) {
        this._modalSelectedVariant = matched;
        // A free-slot variant must sit inside the free price range. Keep it selected so
        // a click on the (disabled-looking) button explains why it can't be added.
        const freeError = isFree ? this._freePriceError(matched.price) : '';
        if (freeError) {
          if (modalAddBtn) {
            modalAddBtn.textContent = 'NOT ELIGIBLE AS FREE ITEM';
            modalAddBtn.classList.add('b2g2-btn-disabled');
          }
          if (priceEl) {
            priceEl.textContent = this.formatMoney(matched.price);
            priceEl.style.color = '#1a1a1a';
          }
          if (compPriceEl) compPriceEl.style.display = 'none';
          this._showMessage(freeError);
          return;
        }
        if (modalAddBtn) {
          modalAddBtn.textContent = 'ADD TO BOX';
          modalAddBtn.classList.remove('b2g2-btn-disabled');
        }
        // Update modal price
        if (priceEl) {
          priceEl.textContent = isFree ? 'FREE' : this.formatMoney(matched.price);
          priceEl.style.color = isFree ? '#2e7d32' : '#1a1a1a';
        }
        if (compPriceEl) {
          if (matched.compare_at_price > matched.price || isFree) {
            compPriceEl.textContent = this.formatMoney(isFree ? matched.price : matched.compare_at_price);
            compPriceEl.style.display = 'inline';
          } else {
            compPriceEl.style.display = 'none';
          }
        }
      } else if (matched && !matched.available) {
        this._modalSelectedVariant = null;
        if (modalAddBtn) {
          modalAddBtn.textContent = 'OUT OF STOCK';
          modalAddBtn.classList.add('b2g2-btn-disabled');
        }
      } else {
        this._modalSelectedVariant = null;
        if (modalAddBtn) {
          modalAddBtn.textContent = 'UNAVAILABLE';
          modalAddBtn.classList.add('b2g2-btn-disabled');
        }
      }
    }

    closeQuickLook() {
      if (!this.modalOverlay) return;
      this.modalOverlay.classList.remove('is-open');
      document.body.style.overflow = '';
      this._currentModalCard = null;
    }

    /* ---- Card Actions ---- */
    handleCardAction(card, type, action) {
      if (action === 'add') {
        // Out of stock: never add it, and say why.
        if (card.classList.contains('is-sold-out')) {
          this._showMessage('"' + (card.dataset.title || 'This item') + '" is out of stock.');
          return;
        }
        // If multi-variant product without selected variant, open Quick Look
        if (card.classList.contains('b2g2-needs-variant') || !card.dataset.variantId) {
          this.openQuickLook(card);
          return;
        }
        // Block if sold out variant
        if (card.classList.contains('is-sold-out')) return;

        this.addItem(type, {
          variantId: card.dataset.variantId,
          productId: card.dataset.productId,
          price: parseInt(card.dataset.price, 10),
          comparePrice: parseInt(card.dataset.comparePrice, 10) || 0,
          title: card.dataset.title,
          image: card.dataset.image,
          element: card,
        });
      } else if (action === 'remove') {
        this.removeItem(type, card.dataset.variantId);
      }
    }

    /* ---- Pick validation ---- */

    // Returns '' when a free-slot item priced `priceCents` is allowed, otherwise a message.
    // omitPrice drops the leading "This option costs X." (used when several items are named).
    _freePriceError(priceCents, omitPrice) {
      const cents = Number(priceCents);
      if (!isFinite(cents)) return "We could not read this item's price, so it cannot be added as a free item.";
      const min = Number(this.config.freeMinPrice) || 0;
      const max = this._effectiveFreeMax();
      const adminMax = Number(this.config.freeMaxPrice) || 0;
      // The admin cap only drops below its own value because of a paid pick
      const cappedByPaid = max !== Infinity && max < adminMax;
      const rupees = cents / 100;
      const costs = omitPrice ? '' : 'This option costs ' + this.formatMoney(cents) + '. ';

      if (max !== Infinity && max < min) {
        return cappedByPaid
          ? 'Free items start at ' + this.formatMoney(min * 100) + ', which is more than your cheapest paid pick (' +
            this.formatMoney(max * 100) + '). Pick paid products priced ' + this.formatMoney(min * 100) + ' or more to choose free items.'
          : 'No free items are available in this price range right now.';
      }
      if (rupees < min) {
        return costs + 'Free items must cost at least ' + this.formatMoney(min * 100) + '.';
      }
      if (max !== Infinity && rupees > max) {
        return costs + (cappedByPaid
          ? 'A free item cannot cost more than your cheapest paid pick (' + this.formatMoney(max * 100) + ').'
          : 'Free items can cost up to ' + this.formatMoney(max * 100) + '.');
      }
      return '';
    }

    // Problems that make an item unfit for its slot, regardless of how full the box is.
    _itemProblem(type, item, omitPrice) {
      if (type === 'free') return this._freePriceError(item ? item.price : NaN, omitPrice);
      return '';
    }

    // null when the item can be added; { full: true } when the slots are full;
    // { message } when the item itself is not allowed.
    _addBlockReason(type, item) {
      const list = type === 'paid' ? this.paidItems : this.freeItems;
      const max = type === 'paid' ? B2G2_MAX_PAID : B2G2_MAX_FREE;
      if (list.length >= max) return { full: true };
      const message = this._itemProblem(type, item);
      return message ? { message } : null;
    }

    _showMessage(message) {
      if (!message) return;
      let toast = document.getElementById('b2g2-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'b2g2-toast';
        toast.className = 'b2g2-toast';
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');
        toast.setAttribute('aria-atomic', 'true');
        document.body.appendChild(toast);
      }
      clearTimeout(this._toastShowTimer);
      clearTimeout(this._toastTimer);
      clearTimeout(this._toastClearTimer);
      // Clear first so repeating the same message is announced again. A timer (not
      // requestAnimationFrame) keeps show-before-hide ordering in background tabs.
      toast.textContent = '';
      this._toastShowTimer = setTimeout(() => {
        toast.textContent = message;
        toast.classList.add('is-visible');
      }, 30);
      this._toastTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
        this._toastClearTimer = setTimeout(() => { toast.textContent = ''; }, 250);
      }, 5000);
    }

    // Returns true when the item was added.
    addItem(type, item) {
      const list = type === 'paid' ? this.paidItems : this.freeItems;
      const block = this._addBlockReason(type, item);

      if (block && block.full) {
        this.shakeElement(this.actionBtn);
        return false;
      }
      if (block) {
        this._showMessage(block.message);
        this.shakeElement(item && item.element ? item.element : this.actionBtn);
        return false;
      }

      item.uid = ++this.uidCounter;
      list.push(item);
      item.element.classList.add('is-selected');

      this.updateCardQty(item.element, type);
      this.updateUI();

      // Dynamic free price cap: recalculate when paid items change
      if (type === 'paid') {
        this._updateFreePriceCap();
      }

      if (type === 'paid' && this.paidItems.length === B2G2_MAX_PAID && this.currentStep === 1) {
        // Re-check when the timer fires: a paid pick removed within the delay must not
        // open step 2 with only one paid item.
        setTimeout(() => {
          if (this.paidItems.length === B2G2_MAX_PAID && this.currentStep === 1) this.goToStep(2);
        }, 350);
      }

      if (this.paidItems.length === B2G2_MAX_PAID && this.freeItems.length === B2G2_MAX_FREE) {
        this.triggerConfetti();
      }
      return true;
    }

    removeItem(type, variantId) {
      const list = type === 'paid' ? this.paidItems : this.freeItems;
      // Last match. Not Array#findLastIndex: iOS Safari only has it from 15.4.
      let idx = list.length - 1;
      while (idx >= 0 && list[idx].variantId !== variantId) idx--;
      if (idx === -1) return;

      const item = list[idx];
      list.splice(idx, 1);

      const remaining = list.filter((i) => i.variantId === variantId).length;
      if (remaining === 0) {
        item.element.classList.remove('is-selected');
      }
      this.updateCardQty(item.element, type);
      this.updateUI();

      if (type === 'paid' && this.paidItems.length < B2G2_MAX_PAID && this.currentStep === 2) {
        this.freeItems.forEach((fi) => {
          fi.element.classList.remove('is-selected');
          this.updateCardQty(fi.element, 'free');
        });
        this.freeItems = [];
        this.goToStep(1);
      }
    }

    updateCardQty(cardEl, type) {
      const list = type === 'paid' ? this.paidItems : this.freeItems;
      const qty = list.filter((i) => i.variantId === cardEl.dataset.variantId).length;
      const qtyText = cardEl.querySelector('.b2g2-qty-text');
      if (qtyText) {
        qtyText.textContent = qty + ' added';
      }
      const max = type === 'paid' ? B2G2_MAX_PAID : B2G2_MAX_FREE;
      const plusBtn = cardEl.querySelector('.b2g2-qty-btn[data-b2g2-action="add"]');
      if (plusBtn) {
        plusBtn.disabled = list.length >= max;
      }
    }

    handleSlotRemove(slot) {
      const slotType = slot.dataset.slotType;
      const slotIndex = parseInt(slot.dataset.slotIndex, 10);
      const list = slotType === 'paid' ? this.paidItems : this.freeItems;

      if (list[slotIndex]) {
        this.removeItem(slotType, list[slotIndex].variantId);
      }
    }

    /* ---- Step switching ---- */
    goToStep(step) {
      this.currentStep = step;

      if (step === 1) {
        if (this.freeStepEl) {
          this.freeStepEl.classList.add('is-hidden');
          this.freeStepEl.classList.remove('is-entering');
        }
        if (this.paidStepEl) {
          this.paidStepEl.classList.remove('is-hidden');
          this.paidStepEl.classList.add('is-entering');
        }
      } else {
        if (this.paidStepEl) {
          this.paidStepEl.classList.add('is-hidden');
          this.paidStepEl.classList.remove('is-entering');
        }
        if (this.freeStepEl) {
          this.freeStepEl.classList.remove('is-hidden');
          this.freeStepEl.classList.add('is-entering');
          this.freeStepEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Lazy-load free tabs on first visit; re-filter on return visits
        var freeState = this._gridState && this._gridState.free;
        if (freeState) {
          if (!freeState.activeHandle) {
            var freeTabsEl = document.getElementById('b2g2-free-tabs');
            if (freeTabsEl) {
              var firstTab = freeTabsEl.querySelector('.b2g2-filter-btn');
              if (firstTab) this._switchCategory('free', firstTab.dataset.collection);
            }
          } else {
            this._updateFreePriceCap();
          }
        }
      }

      this.updateProgress();
      this.updateUI();
    }

    updateProgress() {
      if (this.progressStep1) {
        this.progressStep1.classList.toggle('is-active', this.currentStep === 1);
        this.progressStep1.classList.toggle('is-done', this.currentStep === 2);
      }
      if (this.progressStep2) {
        this.progressStep2.classList.toggle('is-active', this.currentStep === 2);
        this.progressStep2.classList.remove('is-done');
      }
      if (this.progressLine) {
        this.progressLine.classList.toggle('is-filled', this.currentStep === 2);
      }
    }

    /* ---- Action button handler ---- */
    handleActionClick() {
      if (this.currentStep === 1 && this.paidItems.length === B2G2_MAX_PAID) {
        this.goToStep(2);
      } else if (this.currentStep === 2 && this.paidItems.length === B2G2_MAX_PAID && this.freeItems.length === B2G2_MAX_FREE) {
        this.handleAddToCart();
      }
    }

    /* ---- UI updates ---- */
    updateUI() {
      this.updateSlots();
      this.updateCounters();
      this.updateBarInfo();
      this.updateActionButton();
      this.updateCardDisabledStates();
    }

    updateSlots() {
      this.slots.paid.forEach((slot, i) => {
        if (!slot) return;
        const item = this.paidItems[i];
        const img = slot.querySelector('.b2g2-slot-img');
        slot.dataset.slotType = 'paid';
        slot.dataset.slotIndex = i;
        if (item) {
          slot.classList.add('is-filled');
          if (img) { img.src = item.image; img.alt = item.title; }
        } else {
          slot.classList.remove('is-filled');
          if (img) img.src = '';
        }
      });

      this.slots.free.forEach((slot, i) => {
        if (!slot) return;
        const item = this.freeItems[i];
        const img = slot.querySelector('.b2g2-slot-img');
        slot.dataset.slotType = 'free';
        slot.dataset.slotIndex = i;
        if (item) {
          slot.classList.add('is-filled');
          if (img) { img.src = item.image; img.alt = item.title; }
        } else {
          slot.classList.remove('is-filled');
          if (img) img.src = '';
        }
      });
    }

    updateCounters() {
      if (this.paidCounter)
        this.paidCounter.textContent = '(' + this.paidItems.length + '/' + B2G2_MAX_PAID + ')';
      if (this.freeCounter)
        this.freeCounter.textContent = '(' + this.freeItems.length + '/' + B2G2_MAX_FREE + ')';
    }

    updateBarInfo() {
      if (!this.barInfo) return;
      const totalItems = this.paidItems.length + this.freeItems.length;
      const barInner = this.bottomBar.querySelector('.b2g2-bar-inner');

      if (totalItems > 0) {
        this.barInfo.style.display = 'block';
        if (barInner) barInner.classList.add('has-items');

        let paidTotal = 0, originalTotal = 0;
        this.paidItems.forEach((item) => { paidTotal += item.price; originalTotal += item.price; });
        this.freeItems.forEach((item) => { originalTotal += item.price; });
        const savings = originalTotal - paidTotal;

        if (this.totalEl) this.totalEl.textContent = this.formatMoney(paidTotal);
        if (this.originalTotalEl) {
          this.originalTotalEl.textContent = this.formatMoney(originalTotal);
          this.originalTotalEl.style.display = savings > 0 ? 'inline' : 'none';
        }
        if (this.savingsBadge) {
          this.savingsBadge.textContent = 'Save ' + this.formatMoney(savings);
          this.savingsBadge.style.display = savings > 0 ? 'inline-block' : 'none';
        }
      } else {
        this.barInfo.style.display = 'none';
        if (barInner) barInner.classList.remove('has-items');
      }
    }

    updateActionButton() {
      if (!this.actionBtn) return;

      if (this.currentStep === 1) {
        const remaining = B2G2_MAX_PAID - this.paidItems.length;
        if (remaining === 0) {
          this.actionBtn.textContent = 'Choose Free Products →';
          this.actionBtn.disabled = false;
          this.actionBtn.classList.add('is-ready');
        } else {
          this.actionBtn.textContent = 'Select ' + remaining + ' More Product' + (remaining > 1 ? 's' : '');
          this.actionBtn.disabled = true;
          this.actionBtn.classList.remove('is-ready');
        }
      } else {
        const remaining = B2G2_MAX_FREE - this.freeItems.length;
        if (remaining === 0) {
          this.actionBtn.textContent = 'Add To Cart';
          this.actionBtn.disabled = false;
          this.actionBtn.classList.add('is-ready');
        } else {
          this.actionBtn.textContent = 'Pick ' + remaining + ' Free Item' + (remaining > 1 ? 's' : '');
          this.actionBtn.disabled = true;
          this.actionBtn.classList.remove('is-ready');
        }
      }
    }

    updateCardDisabledStates() {
      if (this.paidGrid) {
        this.paidGrid.querySelectorAll('.b2g2-card').forEach((card) => {
          if (card.classList.contains('is-hidden')) return;
          if (this.paidItems.length >= B2G2_MAX_PAID && !card.classList.contains('is-selected')) {
            card.classList.add('is-disabled');
          } else {
            card.classList.remove('is-disabled');
          }
        });
      }

      if (this.freeGrid) {
        this.freeGrid.querySelectorAll('.b2g2-card').forEach((card) => {
          if (card.classList.contains('is-hidden')) return;
          if (this.freeItems.length >= B2G2_MAX_FREE && !card.classList.contains('is-selected')) {
            card.classList.add('is-disabled');
          } else {
            card.classList.remove('is-disabled');
          }
        });
      }
    }

    /* ---- Add to Cart ---- */
    async handleAddToCart() {
      if (this.isAddingToCart) return;
      if (this.paidItems.length < B2G2_MAX_PAID || this.freeItems.length < B2G2_MAX_FREE) return;

      // Last check before the cart: every pick must still fit its slot
      // (free items inside the free price range).
      let problem = '';
      [['paid', this.paidItems], ['free', this.freeItems]].some(([slotType, list]) => list.some((item) => {
        const p = this._itemProblem(slotType, item, true);
        if (p) problem = '"' + (item.title || 'An item') + '" cannot be added. ' + p;
        return !!p;
      }));
      if (problem) {
        this._showMessage(problem);
        this.shakeElement(this.actionBtn);
        return;
      }

      this.isAddingToCart = true;
      this.actionBtn.classList.add('is-loading');
      this.actionBtn.textContent = 'Adding...';

      try {
        const items = [];
        this.paidItems.forEach((item, i) => {
          items.push({
            id: parseInt(item.variantId, 10),
            quantity: 1,
            properties: { _b2g2_bundle_id: this.bundleId, _b2g2_type: 'paid', _b2g2_index: 'P' + (i + 1) },
          });
        });
        this.freeItems.forEach((item, i) => {
          items.push({
            id: parseInt(item.variantId, 10),
            quantity: 1,
            properties: { _b2g2_bundle_id: this.bundleId, _b2g2_type: 'free', _b2g2_index: 'F' + (i + 1) },
          });
        });

        const sectionsToFetch = ['cart-drawer', 'cart-icon-bubble'];
        // XMLHttpRequest, not fetch: the GoKwik and Bundler apps wrap window.fetch and can break this reply
        // (assets/b2g2-cart-bundle.js avoids fetch for the same reason).
        const response = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', '/cart/add.js', true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Accept', 'application/json');
          xhr.timeout = 20000;
          xhr.onload = () => resolve({ ok: xhr.status >= 200 && xhr.status < 300, text: xhr.responseText });
          xhr.onerror = () => reject(new Error('Network error while adding to cart'));
          xhr.ontimeout = xhr.onerror;
          xhr.send(JSON.stringify({ items, sections: sectionsToFetch, sections_url: window.location.pathname }));
        });

        // Shopify explains a refused add in the JSON body (for example a sold-out item), also on HTTP 422.
        let result = null;
        try { result = JSON.parse(response.text); } catch (e) { result = null; }
        // Shopify cart errors carry a status field in the JSON body
        if (!response.ok || !result || result.status) {
          const failure = new Error((result && (result.description || result.message)) || 'Failed to add items to cart');
          failure.shopifyReason = (result && (result.description || result.message)) || '';
          throw failure;
        }

        // The items are in the cart now: a problem while repainting the page must not look like a failed add
        // (a retry would add everything a second time).
        this.bundleId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
        try {
          this.resetSelections();
          this.openCartDrawer(result.sections);
        } catch (uiErr) {
          console.error('B2G2 cart refresh error:', uiErr);
          window.location.href = '/cart';
        }
      } catch (err) {
        console.error('B2G2 Add to Cart error:', err);
        this.actionBtn.textContent = 'Error - Try Again';
        // Tell the shopper why (Shopify's own words, e.g. which item is sold out), not just "Error".
        this._showMessage(err && err.shopifyReason
          ? err.shopifyReason
          : 'Could not add your items. Please check your connection and try again.');
        setTimeout(() => this.updateActionButton(), 2000);
      } finally {
        this.isAddingToCart = false;
        this.actionBtn.classList.remove('is-loading');
      }
    }

    resetSelections() {
      this.paidItems.forEach((item) => {
        item.element.classList.remove('is-selected');
        this.updateCardQty(item.element, 'paid');
      });
      this.freeItems.forEach((item) => {
        item.element.classList.remove('is-selected');
        this.updateCardQty(item.element, 'free');
      });
      this.paidItems = [];
      this.freeItems = [];
      this.goToStep(1);
      this.updateUI();
    }

    openCartDrawer(sections) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') {
        window.location.href = '/cart';
        return;
      }

      // The drawer's own renderContents (assets/cart-drawer.js) swaps #CartDrawer and the
      // cart bubble, re-binds the overlay's click-to-close (a bare #CartDrawer swap loses
      // it) and opens the drawer. It needs both sections.
      const render = (drawerHtml, bubbleHtml) => {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents({ sections: { 'cart-drawer': drawerHtml, 'cart-icon-bubble': bubbleHtml } });
      };
      const goToCart = (err) => {
        console.error('Cart drawer refresh error:', err);
        window.location.href = '/cart';
      };

      // If sections were returned from add.js, use them directly (no extra fetches)
      if (sections && sections['cart-drawer'] && sections['cart-icon-bubble']) {
        try {
          render(sections['cart-drawer'], sections['cart-icon-bubble']);
        } catch (err) {
          goToCart(err);
        }
        return;
      }

      // Fallback (a section came back null): fetch both sections in parallel
      Promise.all([
        fetch('/cart?section_id=cart-drawer').then((r) => r.text()),
        fetch('/cart?section_id=cart-icon-bubble').then((r) => r.text()),
      ])
        .then(([drawerHtml, bubbleHtml]) => render(drawerHtml, bubbleHtml))
        .catch(goToCart);
    }

    /* ---- AJAX Product Loading with Category Tabs ---- */
    _initAjaxLoad() {
      // IntersectionObserver for lazy scroll rendering
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const k = entry.target.dataset.key;
          this._io.unobserve(entry.target);
          entry.target.remove();
          this._renderNextBatch(k);
        });
      }, { rootMargin: '1200px' });

      // Scroll fallback, checked at most once per frame
      let scrollQueued = false;
      const scrollCheck = () => {
        scrollQueued = false;
        ['paid', 'free'].forEach((k) => {
          const st = this._gridState[k];
          if (!st || !st.activeHandle || st.offset >= st.displayed.length) return;
          const grid = document.getElementById(st.gridId);
          // The hidden step is display: none, so its sentinel would always read as near
          // the viewport and render batches nobody can see.
          if (!grid || grid.closest('.b2g2-step.is-hidden')) return;
          const sentinel = grid.querySelector('.b2g2-sentinel');
          if (!sentinel) return;
          const r = sentinel.getBoundingClientRect();
          if (r.top < window.innerHeight + 1200) this._renderNextBatch(k);
        });
      };
      this._scrollRender = () => {
        if (scrollQueued) return;
        scrollQueued = true;
        requestAnimationFrame(scrollCheck);
      };
      window.addEventListener('scroll', this._scrollRender, { passive: true });

      // Collection cache: { handle: { products: [], fetchPage: N, fetchDone: bool } }
      this._collectionCache = {};

      // Grid state per side
      this._gridState = {
        paid: {
          gridId: 'b2g2-paid-grid',
          cardType: 'paid',
          activeHandle: null,
          sortOrder: 'featured',
          all: [],
          displayed: [],
          offset: 0,
          batchSize: 60,
        },
        free: {
          gridId: 'b2g2-free-grid',
          cardType: 'free',
          activeHandle: null,
          sortOrder: 'featured',
          all: [],
          displayed: [],
          offset: 0,
          batchSize: 60,
        }
      };

      this._initCategoryTabs();
      this._bindSortFilter();
    }

    _initCategoryTabs() {
      const self = this;
      // Paid tabs
      const paidTabsEl = document.getElementById('b2g2-paid-tabs');
      if (paidTabsEl) {
        const paidBtns = paidTabsEl.querySelectorAll('.b2g2-filter-btn');
        paidBtns.forEach((tab) => {
          tab.addEventListener('click', () => {
            paidTabsEl.querySelectorAll('.b2g2-filter-btn').forEach((b) => b.classList.remove('is-active'));
            tab.classList.add('is-active');
            self._switchCategory('paid', tab.dataset.collection);
          });
        });
        // Auto-load first paid tab
        if (paidBtns.length > 0) {
          this._switchCategory('paid', paidBtns[0].dataset.collection);
        }
      }

      // Free tabs — bound here but NOT auto-loaded (lazy: loaded when step 2 is shown)
      const freeTabsEl = document.getElementById('b2g2-free-tabs');
      if (freeTabsEl) {
        const freeBtns = freeTabsEl.querySelectorAll('.b2g2-filter-btn');
        freeBtns.forEach((tab) => {
          tab.addEventListener('click', () => {
            freeTabsEl.querySelectorAll('.b2g2-filter-btn').forEach((b) => b.classList.remove('is-active'));
            tab.classList.add('is-active');
            self._switchCategory('free', tab.dataset.collection);
          });
        });
      }
    }

    _switchCategory(key, handle) {
      if (!handle) return;
      const state = this._gridState[key];
      state.activeHandle = handle;
      state.offset = 0;
      state.all = [];
      state.displayed = [];

      const grid = document.getElementById(state.gridId);
      if (!grid) return;

      // Clear grid
      const oldSentinel = grid.querySelector('.b2g2-sentinel');
      if (oldSentinel) this._io.unobserve(oldSentinel);
      grid.innerHTML = '';

      // Check cache
      const cache = this._collectionCache[handle];
      if (cache && cache.fetchDone) {
        this._applyCollectionToGrid(key, cache.products);
        return;
      }

      // Show spinner and fetch
      grid.innerHTML = '<div class="b2g2-loading"><div class="b2g2-spinner"></div><p>Loading products\u2026</p></div>';
      const self = this;
      this._fetchCollectionAll(handle, () => {
        if (state.activeHandle === handle) {
          self._applyCollectionToGrid(key, self._collectionCache[handle].products);
        }
      });
    }

    _fetchCollectionAll(handle, callback) {
      if (!this._collectionCache[handle]) {
        this._collectionCache[handle] = { products: [], fetchPage: 1, fetchDone: false };
      }
      const cache = this._collectionCache[handle];
      if (cache.fetchDone) { if (callback) callback(); return; }

      const fetchPage = () => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', '/collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + cache.fetchPage, true);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              const raw = data.products || [];
              cache.products = cache.products.concat(raw);
              if (raw.length < 250) {
                cache.fetchDone = true;
                if (callback) callback();
              } else {
                cache.fetchPage++;
                fetchPage();
              }
            } catch (e) {
              cache.fetchDone = true;
              if (callback) callback();
            }
          } else {
            cache.fetchDone = true;
            if (callback) callback();
          }
        };
        xhr.onerror = () => {
          cache.fetchDone = true;
          if (callback) callback();
        };
        xhr.send();
      };
      fetchPage();
    }

    _applyCollectionToGrid(key, products) {
      const state = this._gridState[key];
      state.all = products || [];
      const min = this.config.freeMinPrice || 0;
      const max = this._effectiveFreeMax();

      // For free side: apply price filter
      if (key === 'free') {
        state.displayed = state.all.filter((p) => {
          const v = p.variants.find((v) => v.available) || p.variants[0];
          if (!v) return false;
          const priceRupees = parseFloat(v.price);
          return priceRupees >= min && (max === Infinity || priceRupees <= max);
        });
      } else {
        state.displayed = state.all.slice();
      }

      // Apply sort
      this._sortDisplayed(key);
      state.offset = 0;

      // Clear grid and render
      const grid = document.getElementById(state.gridId);
      if (grid) grid.innerHTML = '';

      if (state.displayed.length === 0) {
        let emptyText = 'No products available in this category.';
        if (key === 'free') {
          // The free cap is the cheapest paid pick; below the free minimum nothing can match
          emptyText = (max !== Infinity && max < min)
            ? this._freePriceError(min * 100)
            : 'No products available in this price range.';
        }
        const p = document.createElement('p');
        p.className = 'b2g2-no-products';
        p.textContent = emptyText;
        if (grid) grid.appendChild(p);
        return;
      }

      this._renderNextBatch(key);
    }

    _sortDisplayed(key) {
      const state = this._gridState[key];
      const sort = state.sortOrder || 'featured';
      if (sort === 'price-asc') {
        state.displayed.sort((a, b) => {
          const pa = a.variants.find((v) => v.available) || a.variants[0];
          const pb = b.variants.find((v) => v.available) || b.variants[0];
          const priceA = pa ? parseFloat(pa.price) : Infinity;
          const priceB = pb ? parseFloat(pb.price) : Infinity;
          return priceA - priceB;
        });
      } else if (sort === 'price-desc') {
        state.displayed.sort((a, b) => {
          const pa = a.variants.find((v) => v.available) || a.variants[0];
          const pb = b.variants.find((v) => v.available) || b.variants[0];
          const priceA = pa ? parseFloat(pa.price) : -Infinity;
          const priceB = pb ? parseFloat(pb.price) : -Infinity;
          return priceB - priceA;
        });
      }
    }

    _effectiveFreeMax() {
      const adminMax = this.config.freeMaxPrice;
      if (!adminMax || adminMax <= 0) return Infinity;
      if (this.paidItems.length === 0) return adminMax;
      const cheapestPaid = Math.min(...this.paidItems.map((i) => i.price / 100));
      return Math.min(adminMax, cheapestPaid);
    }

    _updateFreePriceCap() {
      // Remove free picks that no longer fit the free range (the cap follows the
      // cheapest paid pick), and tell the customer instead of dropping them silently.
      const removedTitles = [];
      let reason = '';
      for (let i = this.freeItems.length - 1; i >= 0; i--) {
        const problem = this._itemProblem('free', this.freeItems[i], true);
        if (problem) {
          const removed = this.freeItems.splice(i, 1)[0];
          if (!this.freeItems.some((fi) => fi.variantId === removed.variantId)) {
            removed.element.classList.remove('is-selected');
          }
          this.updateCardQty(removed.element, 'free');
          removedTitles.unshift(removed.title || 'A free item');
          reason = problem;
        }
      }
      if (removedTitles.length) {
        this.updateUI();
        this._showMessage(
          (removedTitles.length === 1
            ? '"' + removedTitles[0] + '" was removed from your free items. '
            : removedTitles.length + ' free items were removed. ') + reason
        );
      }

      // Re-filter and re-render free grid
      const freeState = this._gridState.free;
      if (freeState && freeState.activeHandle && freeState.all.length > 0) {
        this._applyCollectionToGrid('free', freeState.all);
      }
    }

    _renderNextBatch(key) {
      const state = this._gridState[key];
      const grid = document.getElementById(state.gridId);
      if (!grid) return;

      // Remove loading spinner on first render
      const spinner = grid.querySelector('.b2g2-loading');
      if (spinner) spinner.remove();

      // Remove stale sentinel
      const oldSentinel = grid.querySelector('.b2g2-sentinel');
      if (oldSentinel) { this._io.unobserve(oldSentinel); oldSentinel.remove(); }

      const batch = state.displayed.slice(state.offset, state.offset + state.batchSize);
      state.offset += batch.length;

      if (batch.length === 0) {
        if (state.displayed.length === 0) {
          grid.innerHTML = '<p class="b2g2-no-products">No products found.</p>';
        }
        return;
      }

      batch.forEach((product) => {
        const html = this._buildCardHtml(product, state.cardType);
        if (!html) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const card = tmp.firstElementChild;
        if (!card) return;
        grid.appendChild(card);
      });

      // Restore selected state for re-rendered cards
      this._restoreSelectedState(key);

      // Add sentinel if more to render
      if (state.offset < state.displayed.length) {
        const sentinel = document.createElement('div');
        sentinel.className = 'b2g2-sentinel';
        sentinel.dataset.key = key;
        grid.appendChild(sentinel);
        this._io.observe(sentinel);
      }
    }

    _restoreSelectedState(key) {
      const list = key === 'paid' ? this.paidItems : this.freeItems;
      if (list.length === 0) return;
      const grid = document.getElementById(this._gridState[key].gridId);
      if (!grid) return;
      const selectedIds = {};
      list.forEach((item) => { selectedIds[item.variantId] = true; });
      grid.querySelectorAll('.b2g2-card').forEach((card) => {
        if (selectedIds[card.dataset.variantId]) {
          card.classList.add('is-selected');
          list.forEach((item) => {
            if (item.variantId === card.dataset.variantId) {
              item.element = card;
            }
          });
        }
      });
    }

    _buildCardHtml(product, cardType) {
      // Prefer available variant; fall back to first variant so out-of-stock products still render
      const variant = product.variants.find((v) => v.available) || product.variants[0] || null;
      if (!variant) return '';
      const isSoldOut = !product.variants.some((v) => v.available);
      const hasMultipleVariants = product.variants.length > 1;
      const priceCents = Math.round(parseFloat(variant.price) * 100);
      const compareCents = variant.compare_at_price ? Math.round(parseFloat(variant.compare_at_price) * 100) : 0;
      const ptype = product.product_type || '';
      const ptypeKey = ptype.toLowerCase().trim().replace(/\s+/g, ' ');
      const imgs = (product.images || []).map((img) => this._cdnImg(img.src, 600));
      const featImg = imgs[0] || '';
      const secImg = imgs[1] || '';
      const imgsJson = JSON.stringify(imgs).replace(/'/g, '&#39;');
      const rawDesc = (product.body_html || '').replace(/<[^>]+>/g, '').trim().substring(0, 500);
      const titleEsc = this._esc(product.title || '');
      const descEsc = this._esc(rawDesc);
      // If multiple variants, don't pre-select a variant — require user to choose via Quick Look
      const initialVariantId = hasMultipleVariants ? '' : variant.id;
      const needsVariantSelection = hasMultipleVariants;

      // Build variant & options JSON data attrs for multi-variant products
      let variantDataAttrs = '';
      if (hasMultipleVariants) {
        const variantsData = product.variants.map((v) => ({
          id: v.id, available: v.available,
          option1: v.option1 || null, option2: v.option2 || null, option3: v.option3 || null,
          price: Math.round(parseFloat(v.price) * 100),
          compare_at_price: v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : 0,
        }));
        const optionsData = (product.options || []).map((o) => ({ name: o.name, values: o.values }));
        variantDataAttrs = " data-variants-json='" + JSON.stringify(variantsData).replace(/'/g, '&#39;') + "'" +
          " data-options-json='" + JSON.stringify(optionsData).replace(/'/g, '&#39;') + "'";
      }

      const commonAttrs =
        ' data-product-id="' + product.id + '"' +
        ' data-variant-id="' + initialVariantId + '"' +
        ' data-price="' + priceCents + '"' +
        ' data-compare-price="' + compareCents + '"' +
        ' data-title="' + titleEsc + '"' +
        ' data-image="' + featImg + '"' +
        ' data-product-type="' + ptypeKey + '"' +
        " data-images='" + imgsJson + "'" +
        ' data-description="' + descEsc + '"' +
        variantDataAttrs;

      const btnText = isSoldOut ? 'OUT OF STOCK' : (needsVariantSelection ? 'SELECT OPTIONS' : 'ADD TO BOX');
      const btnDisabled = isSoldOut ? ' disabled aria-disabled="true"' : '';

      if (cardType === 'free') {
        return '<div class="b2g2-card b2g2-card--free' + (isSoldOut ? ' is-sold-out' : '') + (needsVariantSelection ? ' b2g2-needs-variant' : '') + '"' + commonAttrs + '>' +
          '<div class="b2g2-card-image">' +
          (isSoldOut ? '<span class="b2g2-sold-out-badge">Out of Stock</span>' : '<span class="b2g2-free-badge">FREE</span>') +
          '<img class="b2g2-card-img-primary" src="' + featImg + '" alt="' + titleEsc + '" loading="lazy" width="400" height="400">' +
          (secImg ? '<img class="b2g2-card-img-secondary" src="' + secImg + '" alt="' + titleEsc + '" loading="lazy" width="400" height="400">' : '') +
          '<span class="b2g2-card-check"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"></polyline></svg></span>' +
          '<button class="b2g2-quick-look-btn" type="button">QUICK LOOK</button>' +
          '</div><div class="b2g2-card-info">' +
          '<h3 class="b2g2-card-title">' + (product.title || '') + '</h3>' +
          '<div class="b2g2-card-price">' +
          '<s class="b2g2-compare-price">' + this.formatMoney(priceCents) + '</s>' +
          '<span class="b2g2-free-label">FREE</span></div>' +
          '<button class="b2g2-add-btn" data-b2g2-action="add" type="button"' + btnDisabled + '>' + btnText + '</button>' +
          '<div class="b2g2-qty-controls">' +
          '<button class="b2g2-qty-btn" data-b2g2-action="remove" type="button">−</button>' +
          '<span class="b2g2-qty-text">1 added</span>' +
          '<button class="b2g2-qty-btn" data-b2g2-action="add" type="button">+</button>' +
          '</div></div></div>';
      }
      return '<div class="b2g2-card' + (isSoldOut ? ' is-sold-out' : '') + (needsVariantSelection ? ' b2g2-needs-variant' : '') + '"' + commonAttrs + '>' +
        '<div class="b2g2-card-image">' +
        (isSoldOut ? '<span class="b2g2-sold-out-badge">Out of Stock</span>' : (compareCents > priceCents ? '<span class="b2g2-badge">SALE</span>' : '')) +
        '<img class="b2g2-card-img-primary" src="' + featImg + '" alt="' + titleEsc + '" loading="lazy" width="400" height="400">' +
        (secImg ? '<img class="b2g2-card-img-secondary" src="' + secImg + '" alt="' + titleEsc + '" loading="lazy" width="400" height="400">' : '') +
        '<span class="b2g2-card-check"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"></polyline></svg></span>' +
        '<button class="b2g2-quick-look-btn" type="button">QUICK LOOK</button>' +
        '</div><div class="b2g2-card-info">' +
        '<h3 class="b2g2-card-title">' + (product.title || '') + '</h3>' +
        '<div class="b2g2-card-price">' +
        (compareCents > priceCents ? '<s class="b2g2-compare-price">' + this.formatMoney(compareCents) + '</s>' : '') +
        '<span class="b2g2-price">' + this.formatMoney(priceCents) + '</span></div>' +
        '<button class="b2g2-add-btn" data-b2g2-action="add" type="button"' + btnDisabled + '>' + btnText + '</button>' +
        '<div class="b2g2-qty-controls">' +
        '<button class="b2g2-qty-btn" data-b2g2-action="remove" type="button">−</button>' +
        '<span class="b2g2-qty-text">1 added</span>' +
        '<button class="b2g2-qty-btn" data-b2g2-action="add" type="button">+</button>' +
        '</div></div></div>';
    }

    _bindSortFilter() {
      ['paid', 'free'].forEach((key) => {
        const sel = document.getElementById('b2g2-' + key + '-sort');
        if (!sel) return;
        sel.addEventListener('change', () => {
          const state = this._gridState && this._gridState[key];
          if (!state || !state.activeHandle) return;
          state.sortOrder = sel.value;
          // Re-apply current collection with new sort
          this._applyCollectionToGrid(key, state.all);
        });
      });
    }

    _showGridError(gridId) {
      const grid = document.getElementById(gridId);
      if (grid) grid.innerHTML = '<p class="b2g2-no-products">Unable to load products. Please refresh the page.</p>';
    }

    _cdnImg(src, size) {
      if (!src) return '';
      return src.replace(/(_\d+x\d*)?(\.(jpg|jpeg|png|gif|webp))(\?|$)/i, '_' + size + 'x$2$4');
    }

    _esc(str) {
      return (str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    /* ---- Helpers ---- */
    formatMoney(amountInSubunit) {
      const amount = amountInSubunit / 100;
      return '\u20B9' + amount.toLocaleString('en-IN', { maximumFractionDigits: 0 });
    }

    triggerConfetti() {
      // Delegates to the shared canvas confetti (assets/confetti.js), which is loaded
      // site-wide and respects the theme settings + prefers-reduced-motion. The old
      // 50-div implementation is kept below purely as a fallback, because
      // buy-2-get-2.css is only present on this template.
      if (window.STJConfetti && typeof window.STJConfetti.burst === 'function') {
        window.STJConfetti.burst();
        return;
      }

      const container = document.createElement('div');
      container.className = 'b2g2-confetti-container';
      document.body.appendChild(container);

      const colors = ['#f44336', '#e91e63', '#9c27b0', '#3f51b5', '#03a9f4', '#4caf50', '#ffeb3b', '#ff9800', '#D4AF37'];
      for (let i = 0; i < 50; i++) {
        const piece = document.createElement('div');
        piece.className = 'b2g2-confetti-piece';
        piece.style.left = Math.random() * 100 + '%';
        piece.style.animationDelay = Math.random() * 1.5 + 's';
        piece.style.animationDuration = 2 + Math.random() * 2 + 's';
        piece.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        piece.style.borderRadius = Math.random() > 0.5 ? '50%' : '0';
        piece.style.width = 5 + Math.random() * 8 + 'px';
        piece.style.height = piece.style.width;
        container.appendChild(piece);
      }
      setTimeout(() => container.remove(), 4500);
    }

    shakeElement(el) {
      if (!el) return;
      el.classList.add('b2g2-shake');
      setTimeout(() => el.classList.remove('b2g2-shake'), 400);
    }
  }

  window.Buy2Get2 = Buy2Get2;
})();
